import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleSpin } from "../../service";

// Thunk for spinning the reels
export const rgsPlay = createAsyncThunk(
  "spin/rgsPlay",
  async (options = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const { bet, token, availableActions } = state.game;
    const { purchasedFeature, bonusCost } = options;

    try {
      // Determine command based on available_actions from API
      // If "respin" is available, use it (feature game); otherwise use "spin" (regular game)
      const command = availableActions.includes("spin") ? "spin" : "respin";

      // Response is already transformed by RGS class
      const result = await handleSpin(bet, token, command, purchasedFeature);

      // Return result with command and bonusCost included (already normalized by RGS)
      return { ...result, command, bonusCost };
    } catch (error) {
      // If error is an object with errors array (API error), return full error object
      // Otherwise return error message
      if (error && typeof error === 'object' && error.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      } else {
        return rejectWithValue(error.message || "Failed to spin the reels");
      }
    }
  }
);

// Initial state for the spin slice
const initialState = {
  isSpinning: false, // Indicates if a spin is currently in progress
  status: "idle", // Tracks the status of the API request
  error: null, // Stores any error messages
  manualStop: false, // Flag to track if the spin was manually stopped
  spinComplete: true, // Indicates if the spin has completed
  fakeSpinMode: false, // Flag to track if current spin is a fake spin (no credits)
  // HoldAndWin feature game states (separate from regular game)
  holdAndWinIsSpinning: false, // Indicates if HoldAndWin spin is in progress
  holdAndWinStatus: "idle", // Tracks the status of HoldAndWin API request
  holdAndWinSpinComplete: true, // Indicates if HoldAndWin spin has completed
  holdAndWinManualStop: false, // Flag to track if HoldAndWin spin was manually stopped
};

// Create a slice for spin-related state and actions
const spinSlice = createSlice({
  name: "spin",
  initialState,
  reducers: {
    // Action to manually stop a spin
    manualSpinStop: (state) => {
      if (state.isSpinning && !state.manualStop) {
        state.manualStop = true; // Always mark as manual stop when requested
      }
    },

    // Action to manually set spinning state
    manualSpinStart: (state) => {
      state.isSpinning = true; // Set isSpinning
      state.spinComplete = false; // Set spinComplete
    },

    // Action to set the spin completion status
    setSpinComplete: (state, action) => {
      if (state.status === "succeeded") {
        state.isSpinning = false; // Mark spinning as false
        state.spinComplete = action.payload; // Update spin completion status
      }
    },

    // Action to trigger a fake spin (no API call needed)
    triggerFakeSpin: (state, action) => {
      state.isSpinning = true;
      state.spinComplete = false;
      state.manualStop = false;
      state.fakeSpinMode = true;
      state.status = "succeeded"; // Set to succeeded immediately since no API call
    },

    // HoldAndWin feature game actions
    // Action to manually stop a HoldAndWin spin
    holdAndWinManualSpinStop: (state) => {
      if (state.holdAndWinIsSpinning && !state.holdAndWinManualStop) {
        state.holdAndWinManualStop = true;
      }
    },

    // Action to set HoldAndWin spin completion status
    holdAndWinSetSpinComplete: (state, action) => {
      if (state.holdAndWinStatus === "succeeded") {
        state.holdAndWinIsSpinning = false;
        state.holdAndWinSpinComplete = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(rgsPlay.pending, (state) => {
        // Check if HoldAndWin was already spinning - if so, this is a HoldAndWin spin
        // Otherwise, assume it's a regular spin (will be corrected in fulfilled if command is "respin")
        const isHoldAndWin = state.holdAndWinIsSpinning;

        if (isHoldAndWin) {
          // Update HoldAndWin states
          state.holdAndWinStatus = "pending";
          state.holdAndWinIsSpinning = true;
          state.holdAndWinSpinComplete = false;
          state.holdAndWinManualStop = false;
        } else {
          // Update regular game states
          state.error = null;
          state.status = "pending";
          state.isSpinning = true;
          state.spinComplete = false;
          state.manualStop = false;
          state.fakeSpinMode = false;
        }
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        // Check command from payload to determine if this is HoldAndWin
        const command = action.payload?.command;
        const isHoldAndWin = command === "respin";

        if (isHoldAndWin) {
          // Update HoldAndWin states, reset regular states
          state.holdAndWinStatus = "succeeded";
          state.holdAndWinIsSpinning = true;
          state.holdAndWinSpinComplete = false;
          state.holdAndWinManualStop = false;

          // Reset regular spin states (they were set in pending but shouldn't be active)
          state.isSpinning = false;
          state.status = "idle";
          state.spinComplete = true;
        } else {
          // Update regular game states, reset HoldAndWin states
          state.status = "succeeded";
          // Check if this was a fake spin and set the flag
          if (action.payload && action.payload.fake) {
            state.fakeSpinMode = true;
          } else {
            state.fakeSpinMode = false;
          }

          // Reset HoldAndWin states (they were set in pending but shouldn't be active)
          state.holdAndWinIsSpinning = false;
          state.holdAndWinStatus = "idle";
          state.holdAndWinSpinComplete = true;
        }
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        // Determine which spin type failed based on current state
        const isHoldAndWin = state.holdAndWinIsSpinning;

        if (isHoldAndWin) {
          // Update HoldAndWin states
          state.holdAndWinStatus = "failed";
          state.holdAndWinIsSpinning = false;
        } else {
          // Update regular game states
          state.status = "failed";
          state.isSpinning = false;
          state.error = action.payload || "Spin failed";
          state.fakeSpinMode = false;
        }
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectIsSpinning = (state) => state.spin.isSpinning;
export const selectSpinStatus = (state) => state.spin.status;
export const selectSpinError = (state) => state.spin.error;
export const selectManualStop = (state) => state.spin.manualStop;
export const selectSpinComplete = (state) => state.spin.spinComplete;
export const selectFakeSpinMode = (state) => state.spin.fakeSpinMode;
export const selectHoldAndWinIsSpinning = (state) =>
  state.spin.holdAndWinIsSpinning;
export const selectHoldAndWinStatus = (state) => state.spin.holdAndWinStatus;
export const selectHoldAndWinSpinComplete = (state) =>
  state.spin.holdAndWinSpinComplete;
export const selectHoldAndWinManualStop = (state) =>
  state.spin.holdAndWinManualStop;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  manualSpinStop,
  manualSpinStart,
  setSpinComplete,
  triggerFakeSpin,
  holdAndWinManualSpinStop,
  holdAndWinSetSpinComplete,
} = spinSlice.actions;

// Export the reducer to be included in the store
export default spinSlice.reducer;
